import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { CampaignCountdown } from "@/components/campaign-card/campaign-countdown";
import { BetFeedbackNotice } from "@/components/campaigns/bet-feedback-notice";
import {
  MinBetPill,
  SegmentedTabs,
  type CampaignTab,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import { Typography } from "@/components/ui/typography";
import { campaignDisplayTitle } from "@/utils/campaign-helpers";
import { CampaignInfoTab, CampaignTermsTab } from "./campaign-tab-bodies";
import { CampaignTabContext } from "./campaign-tab-context";
import type { CampaignView } from "./campaign-view";

/**
 * The overlay's dock column (`--dock-panel` wide), shared by every type: the
 * campaign's name with its countdown, any bet feedback the backend holds for
 * it (on every tab: it explains a bet just placed), and the detail view's
 * Overview / Info / T&C tabs, over a body that scrolls when the game frame is
 * shorter than it.
 * Overview is the type's dock panel (`children`); Info and T&C are the detail
 * view's own, laid out as on phones — the column is phone-narrow at any
 * viewport.
 */
export const CampaignDockFrame = ({
  view,
  children,
}: {
  view: CampaignView;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const key = campaignKey(view.campaign);
  // Another campaign opens on its Overview, as in the detail view.
  const [selected, setSelected] = useState<{
    key: CampaignKey;
    tab: CampaignTab;
  }>({ key, tab: "primary" });
  const tab = selected.key === key ? selected.tab : "primary";
  const selectTab = useCallback(
    (next: CampaignTab) => setSelected({ key, tab: next }),
    [key]
  );

  // Every tab, and every campaign, starts at its top.
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [key, tab]);

  return (
    <CampaignTabContext.Provider value={selectTab}>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-col gap-3 px-3 pt-3">
          <header className="flex items-start justify-between gap-2">
            {/* The name wraps rather than truncates; the countdown keeps its line. */}
            <Typography tag="h3" className="min-w-0 text-base leading-snug">
              {campaignDisplayTitle(view.campaign, t)}
            </Typography>
            <CampaignCountdown
              campaign={view.campaign}
              variant="inline"
              compact
              className="shrink-0 pt-1"
            />
          </header>
          <BetFeedbackNotice campaign={view.campaign} />
          <SegmentedTabs
            tab={tab}
            onChange={selectTab}
            primaryLabel={t("nav.overview", "Overview")}
            className="w-full"
          />
        </div>
        {/* Its sections keep their height (`*:shrink-0`) and the body scrolls: a card clips its
            overflow, which lets a flex item shrink below its content, so a column taller than
            the frame would otherwise squash the cards and cut off their bottoms. */}
        <div
          ref={bodyRef}
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 *:shrink-0"
        >
          {tab === "primary" ? (
            children
          ) : tab === "info" ? (
            <CampaignInfoTab view={view} />
          ) : (
            <CampaignTermsTab campaign={view.campaign} />
          )}
        </div>
      </div>
    </CampaignTabContext.Provider>
  );
};

/**
 * A type's Overview in the dock frame: its own sections, then the qualifying
 * bet as a footer note at the foot of the column.
 */
export const CampaignDockPanel = ({
  minimumBet,
  children,
}: {
  minimumBet?: { value: number; currency: string; display?: string };
  children: ReactNode;
}) => {
  const hasMinBet = (minimumBet?.value ?? 0) > 0;

  return (
    <>
      {children}
      {hasMinBet && (
        <footer className="mt-auto font-body text-xs text-muted-foreground">
          <MinBetPill minimumBet={minimumBet} appearance="text" />
        </footer>
      )}
    </>
  );
};
